import React from 'react';
import GeneralEditWizzardPage from './GeneralEditWizzardPageFigma';
import FigmaButton from '../FigmaButton';
import ButtonColorsEnum from '../../Enums/ButtonColorsEnum';
import ButtonTypeEnum from '../../Enums/ButtonTypeEnum';
import IconPositionEnum from '../../Enums/IconPositionEnum';
import compareHelper from '../../Helpers/compareHelper';

/**
 * Compare variant of the Figma wizard page: the step as two panels — read-only `readOnlyItem` left, the
 * editable form right — with a copy column of per-row buttons between them. No bespoke field rendering:
 * it subclasses `GeneralEditWizzardPageFigma`, and the left panel is another instance of that same
 * component in `embedded` + `readOnly` mode, so per-side visibility (`hideOnReadOnlySide` /
 * `hideOnEditSide`) and inert real inputs come for free. A row whose sides already match gets a disabled
 * button, so the enabled arrows map what differs. A field one column hides but the other shows keeps a
 * blank row, to stay in line; hidden on both sides it leaves no row.
 *
 * Optional window-description statics: readOnlySideLabel ('Imported values'), editSideLabel
 * ('Values to save') — the two panel headers.
 */
class GeneralEditWizzardPageSideBySide extends GeneralEditWizzardPage {
  static defaultProps = {
    ...GeneralEditWizzardPage.defaultProps,
    readOnlyItem: null,
  };

  // Two panels need more room, and the compact centered modal would defeat the point of a comparison.
  getModalMaxWidth() {
    return 1500;
  }

  isCompactModal() {
    return false;
  }

  // Suppressed here so the heading doesn't land inside the right panel; renderForm places it once.
  renderSectionHeader() {
    return null;
  }

  //#region Row alignment
  // Judged against THAT column's own item: a `hide` rule reading the item can differ per dataset.
  isFieldShownOnSide(field, readOnly) {
    const item = readOnly ? this.props.readOnlyItem ?? {} : this.state.editedItem;
    return !this.isFieldHiddenOnSide(field, item, readOnly);
  }

  // This instance IS the editable panel: reserve a row only while the read-only column still shows it.
  reservesSpaceForHiddenField(field) {
    return this.isFieldShownOnSide(field, true);
  }
  //#endregion

  //#region Copy column
  /** A copy button is offered only where the same field is on screen in BOTH columns. */
  isFieldCopyable(field) {
    // A function `fieldName` has no key to write back to; a `valueField` field is a list, not one value.
    if (typeof field.fieldName !== 'string' || field.valueField !== undefined) {
      return false;
    }
    return this.isFieldShownOnSide(field, false) && this.isFieldShownOnSide(field, true);
  }

  /**
   * Such a row's button is rendered but disabled, not removed: greying it out shows at a glance which rows
   * differ, while a missing button would read as "no copy offered here". Equality is the user's, not `===`'s
   * (see compareHelper) — what the two inputs render is what decides.
   */
  isFieldAlreadyIdentical(field) {
    const readOnlyItem = this.props.readOnlyItem ?? {};
    return compareHelper.areValuesEqual(readOnlyItem[field.fieldName], this.state.editedItem[field.fieldName]);
  }

  /**
   * Unconditional by design: whatever the left column holds wins, empty included, so the button also clears
   * a value the import invented. Routed through `changeOfEditedItem` to run the normal change pipeline.
   */
  copyReadOnlyValueToEditedItem(field) {
    const readOnlyItem = this.props.readOnlyItem ?? {};
    this.changeOfEditedItem(field.fieldName, this.copyOfReadOnlyValue(readOnlyItem[field.fieldName]));
  }

  // Deep-copied: the edited item is mutated in place, so sharing the reference would let an edit on the
  // right change what the left shows.
  copyOfReadOnlyValue(value) {
    if (value === null || typeof value !== 'object') {
      return value;
    }
    if (value instanceof Date) {
      return new Date(value.getTime());
    }
    try {
      return typeof structuredClone === 'function' ? structuredClone(value) : JSON.parse(JSON.stringify(value));
    } catch (e) {
      // Not cloneable (a File, something cyclic): share the reference rather than lose the value.
      return value;
    }
  }

  /**
   * The copy column, one button at a time inside the editable field's own cell, moved into the gutter by
   * `.WizardSideBySideCopyCell`. Living in the row is what makes it align with its field exactly — a
   * separate DOM column could not predict the row heights of the other two.
   */
  renderFieldRowAddon(field) {
    if (!this.isFieldCopyable(field)) {
      return null;
    }
    const readOnlySideLabel = this.state.windowDescription.readOnlySideLabel ?? 'Imported values';
    const identical = this.isFieldAlreadyIdentical(field);
    return (
      <div className='WizardSideBySideCopyCell'>
        <FigmaButton
          label='Copy'
          tooltip={
            identical
              ? 'Both sides already hold this value'
              : `Overwrite this field with the value from "${readOnlySideLabel}"`
          }
          type={ButtonTypeEnum.SmallIconOnly}
          color={ButtonColorsEnum.Common}
          iconPosition={IconPositionEnum.Center}
          iconName='fa-solid fa-arrow-right'
          disabled={identical}
          onClick={() => this.copyReadOnlyValueToEditedItem(field)}
        />
      </div>
    );
  }
  //#endregion

  renderReadOnlyPanel() {
    return (
      <GeneralEditWizzardPage
        embedded={true}
        readOnly={true}
        fieldIdPrefix='readonly'
        errorCode={this.props.errorCode}
        step={this.props.step}
        crudType={this.props.crudType}
        windowDescription={this.props.windowDescription}
        editedItem={this.props.readOnlyItem ?? {}}
        visitedSteps={this.props.visitedSteps}
        // Mirror of reservesSpaceForHiddenField: the panel is a plain base page knowing only its own item,
        // so the decision is made here, where both are.
        shouldReserveHiddenFieldSpace={(field) => this.isFieldShownOnSide(field, false)}
      />
    );
  }

  renderForm() {
    const windowDescription = this.state.windowDescription;
    return (
      <div className='EditPanelInput EditPanelInputSideBySide'>
        {super.renderSectionHeader()}
        <div className='WizardSideBySide'>
          <section className='WizardSideBySidePanel WizardSideBySidePanelReadOnly'>
            <header className='WizardSideBySidePanelHead'>
              {windowDescription.readOnlySideLabel ?? 'Imported values'}
            </header>
            {this.renderReadOnlyPanel()}
          </section>
          <section className='WizardSideBySidePanel'>
            <header className='WizardSideBySidePanelHead'>{windowDescription.editSideLabel ?? 'Values to save'}</header>
            {super.renderForm()}
          </section>
        </div>
      </div>
    );
  }
}

export default GeneralEditWizzardPageSideBySide;
